import { Card, Table, Tag, Button, Space, Typography, message, Input, Alert, Tooltip, Popconfirm } from 'antd';
import { useState } from 'react';
import { useFederationPeers } from '../../hooks/use_federation_peers';
import { useNodeInfo } from '../../hooks/use_node_info';
import NodeAvatar from '../Node/NodeAvatar';

const { Title, Text, Paragraph } = Typography;

// Maps a peer status to an Ant Design Tag color (theme tokens, no hardcoded hex).
const STATUS_COLOR = { approved: 'green', pending: 'gold', blocked: 'red', unverified: 'volcano', unreachable: 'volcano' };

// What the peer's own decision about this node means, for the "There" column.
const REMOTE_HELP = {
    approved: 'That node has approved this one: calls both ways work.',
    pending: 'That node knows this one but its superadmin has not approved it yet.',
    blocked: 'That node has blocked this one.',
    unverified: 'That node refused this node\'s signature: it holds another key for this node, or none and is too old to take one from the handshake.',
    unreachable: 'That node did not answer the last handshake.',
};

/**
 * Superadmin-only panel for this node's federation: what this node is to its
 * peers (its id, address, signing key), and the peers themselves — nodes,
 * added by address, each with this node's decision about it ("Here") and its
 * last-known decision about this node ("There"). Renders nothing for
 * non-superadmins (the list endpoint 403s), nor on a node with federation off
 * — there is nothing to peer with — so it is safe to mount unconditionally.
 */
function FederationPeers() {
    const {
        peers, identity, isServerAdmin, loading,
        approve, block, unblock, handshake, addPeer, remove, createKey,
    } = useFederationPeers();
    const { federationEnabled } = useNodeInfo();
    const [busy, setBusy] = useState(null); // server_id currently mutating
    const [baseUrl, setBaseUrl] = useState('');
    const [nodeSlug, setNodeSlug] = useState('');
    const [adding, setAdding] = useState(false);
    const [creatingKey, setCreatingKey] = useState(false);

    // Hide entirely until we know the caller is the server admin, and on a node
    // that isn't federated at all.
    if (!isServerAdmin || !federationEnabled) return null;

    const run = (serverId, action, fn) => {
        setBusy(serverId);
        fn(serverId)
            .then(() => message.success(`Peer ${serverId} ${action}`))
            .catch(err => message.error(err.message || `Could not ${action} ${serverId}`))
            .finally(() => setBusy(null));
    };

    const submitAdd = () => {
        const url = baseUrl.trim();
        if (!url) return;
        setAdding(true);
        addPeer({ base_url: url, node_slug: nodeSlug.trim() })
            .then(peer => {
                setBaseUrl('');
                setNodeSlug('');
                const there = peer?.remote_status;
                message.success(there === 'approved'
                    ? `Peered with ${peer.server_id}`
                    : `Added ${peer?.server_id ?? url}; waiting for its superadmin to approve this node`);
            })
            .catch(err => message.error(err.message || 'Could not add that peer'))
            .finally(() => setAdding(false));
    };

    const makeKey = () => {
        setCreatingKey(true);
        createKey()
            .then(() => message.success('This node now signs with a key of its own'))
            .catch(err => message.error(err.message || 'Could not create a key'))
            .finally(() => setCreatingKey(false));
    };

    const columns = [
        {
            title: 'Peer',
            dataIndex: 'server_id',
            key: 'server_id',
            render: (id, row) => (
                <Space align="center">
                    <NodeAvatar serverId={id} size={32} />
                    <div>
                        <div>
                            <Text strong>{row.name || id}</Text>
                            {row.name && <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>{id}</Text>}
                            {row.node_slug && <Tag style={{ marginLeft: 6, fontSize: 10 }}>node {row.node_slug}</Tag>}
                        </div>
                        {row.base_url && <Text type="secondary" style={{ fontSize: 12 }}>{row.base_url}</Text>}
                        {!row.has_key && (
                            <div><Text type="warning" style={{ fontSize: 12 }}>No signing key known: it cannot be verified until it handshakes.</Text></div>
                        )}
                    </div>
                </Space>
            ),
        },
        {
            title: 'Here',
            dataIndex: 'status',
            key: 'status',
            render: status => <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag>,
        },
        {
            title: 'There',
            dataIndex: 'remote_status',
            key: 'remote_status',
            render: status => (status ? (
                <Tooltip title={REMOTE_HELP[status]}>
                    <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag>
                </Tooltip>
            ) : <Text type="secondary">—</Text>),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, row) => {
                const isBusy = busy === row.server_id;
                return (
                    <Space wrap>
                        {row.status !== 'approved' && (
                            <Button size="small" type="primary" loading={isBusy}
                                onClick={() => run(row.server_id, 'approved', approve)}>
                                Approve
                            </Button>
                        )}
                        {row.status !== 'blocked' && (
                            <Button size="small" danger loading={isBusy}
                                onClick={() => run(row.server_id, 'blocked', block)}>
                                Block
                            </Button>
                        )}
                        {row.status === 'blocked' && (
                            <Button size="small" loading={isBusy}
                                onClick={() => run(row.server_id, 'unblocked', unblock)}>
                                Unblock
                            </Button>
                        )}
                        {row.base_url && (
                            <Button size="small" loading={isBusy}
                                onClick={() => run(row.server_id, 'handshaken', handshake)}>
                                Handshake
                            </Button>
                        )}
                        {!row.from_registry && (
                            <Popconfirm
                                title={`Forget ${row.server_id}?`}
                                description="Its address, key and this node's decision go. Peer again by adding it."
                                onConfirm={() => run(row.server_id, 'removed', remove)}
                            >
                                <Button size="small" loading={isBusy}>Remove</Button>
                            </Popconfirm>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>Federation</Title>
            {identity && (
                <div style={{ marginBottom: 16 }}>
                    <Space align="center">
                        <NodeAvatar serverId={identity.server_id} size={40} />
                        <div>
                            <div>
                                <Text>This node federates as </Text>
                                <Text strong>{identity.server_id || '(no id set)'}</Text>
                                {identity.node_slug && <Tag style={{ marginLeft: 6, fontSize: 10 }}>node {identity.node_slug}</Tag>}
                                <Tag color={identity.key_source === 'node' ? 'green' : identity.key_source === 'deployment' ? 'gold' : 'red'} style={{ marginLeft: 6 }}>
                                    {identity.key_source === 'node' ? 'own key'
                                        : identity.key_source === 'deployment' ? "deployment's shared key" : 'no key'}
                                </Tag>
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {identity.base_url || 'No public API address set (api_public_url): peers cannot be told where to reach this node.'}
                            </Text>
                        </div>
                    </Space>
                    {identity.shares_id_with?.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginTop: 12 }}
                            title={`Peers cannot tell this node from ${identity.shares_id_with.join(', ')}`}
                            description="They federate under the same id. Give each node its own federation_server_id in its node settings."
                        />
                    )}
                    {identity.key_source !== 'node' && (
                        <div style={{ marginTop: 12 }}>
                            <Tooltip title="Peers that already know this node under the deployment's key must remove and add it again afterwards.">
                                <Button size="small" onClick={makeKey} loading={creatingKey}>
                                    Give this node its own signing key
                                </Button>
                            </Tooltip>
                        </div>
                    )}
                </div>
            )}
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                Peers are nodes. Add one by its API address; it says who it is, this node tells it who
                we are, and its superadmin approves us there. Only approved peers can list what this
                node shares, connect, message, or join hangouts here.
            </Paragraph>
            <Space.Compact style={{ width: '100%', maxWidth: 720, marginBottom: 16 }}>
                <Input
                    placeholder="Peer API address, e.g. https://api.example.org"
                    value={baseUrl}
                    onChange={e => setBaseUrl(e.target.value)}
                    onPressEnter={submitAdd}
                />
                <Input
                    style={{ maxWidth: 200 }}
                    placeholder="node slug (if pooled)"
                    value={nodeSlug}
                    onChange={e => setNodeSlug(e.target.value)}
                    onPressEnter={submitAdd}
                />
                <Button type="primary" onClick={submitAdd} loading={adding} disabled={!baseUrl.trim()}>
                    Add peer
                </Button>
            </Space.Compact>
            <Table
                rowKey="server_id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={peers}
                pagination={false}
                locale={{ emptyText: 'No peers yet' }}
            />
        </Card>
    );
}

export default FederationPeers;
